import React, {
    useRef,
    useEffect,
    useCallback,
    useMemo,
    useState,
} from "react";
import FormGroup from "../FormGroup";
import FormControlMessage from "../FormControlMessage";
import Label from "../Label";
import { getClassNames } from "../../utils";

import CurrencyInput from "react-currency-input-field";
import Tooltip from "../Tooltip";
import TextInputIcon from "../TextInput/TextInputIcon";

import "./NumberInput.css";
import withTheme from "../../hocs/withTheme";
import uid from "../../utils/uid";

type ValidationConfig = {
    maxValue?: number | null;
    minValue?: number | null;
};

function NumberInput(props) {
    const {
        suffix = "",
        prefix = "",
        thousandsGroupStyle = "thousand",
        label = null,
        labelInfobox = false,
        placeholder = null,
        value = "",
        defaultValue = undefined,
        disabled = false,
        required = false,
        error = null,
        errorIcon = false,
        errorInTooltip = false,
        questionmark = false,
        verified = false,
        addonAfter = null,
        children = null,
        onChange = (v) => v,
        getFormattedValue = (v) => v,
        withSelectBox = false,
        validation = {
            maxValue: null,
            minValue: null,
        },
        decimalSeparator = ".",
        thousandSeparator = "’",
        helpText = null,
        id,
        addonAfterBorder,
        precision = 2,
        textRight = false,
        allowNegativeValue = true,
        className,
        ...rest
    } = props;

    const inputRef = useRef<HTMLInputElement | null>(null);
    const [inputValue, setInputValue] = useState(value);

    useEffect(() => {
        if (decimalSeparator === thousandSeparator) {
            throw new Error(
                "Privided Decimal and Thousand are equal. Use various separators!"
            );
        }
    }, [decimalSeparator, thousandSeparator]);

    const iconProps = {
        errorInTooltip,
        error,
        errorIcon,
        questionmark,
        verified,
    };

    const onInputFocus = useCallback(
        (e) => {
            if (inputRef && inputRef.current) {
                const start = inputRef.current.selectionStart;
                const end = inputRef.current.selectionEnd;

                // If text is selected go to the end of the input
                if (start === 0 && end === inputRef.current.value.length) {
                    inputRef.current.selectionStart =
                        inputRef.current.value.length;
                    inputRef.current.selectionEnd =
                        inputRef.current.value.length;
                }
            }
        },
        [inputRef]
    );

    const hasSuffix = useMemo(
        () => error || questionmark || verified,
        [error, questionmark, verified]
    );

    const inputId = id || uid("text-input");

    const classNameAddonAfter = getClassNames("TextInput-group-addon", {
        "addon-after-border": addonAfterBorder,
    });

    const numericInputClass = useMemo(() => {
        const inputClass = getClassNames(
            "TextInput",
            { "text-right": textRight },
            className
        );

        if (error) {
            return getClassNames("TextInput", {
                "text-right": textRight,
                errorBorder: error,
            });
        }

        return inputClass;
    }, [error, required, textRight, inputValue]);

    useEffect(() => {
        if (value !== undefined && value !== null) {
            setInputValue(value);
        }
    }, [value]);

    useEffect(() => {
        if (defaultValue !== undefined && defaultValue !== null) {
            setInputValue(defaultValue);
        }
    }, [defaultValue]);

    const inputAllowNegativeValue = useMemo(() => {
        if (
            validation &&
            validation.minValue !== null &&
            validation.minValue !== undefined &&
            Number(validation.minValue) >= 0
        ) {
            return false;
        }

        return allowNegativeValue;
    }, [validation, allowNegativeValue]);

    const defaultNumericValue = useMemo(() => {
        if (
            validation &&
            validation.minValue !== null &&
            validation.minValue !== undefined
        ) {
            return validation.minValue;
        }

        if (defaultValue !== undefined && defaultValue !== null) {
            return defaultValue;
        }

        if (!validation && !defaultValue && required) {
            return 0;
        }

        return null;
    }, [defaultValue, validation, required]);

    const onBlur = useCallback(
        (e) => {
            if (validation) {
                if (!inputValue || inputValue === "" || inputValue === null) {
                    if (
                        validation.minValue !== null &&
                        validation.minValue !== undefined
                    ) {
                        setInputValue(validation.minValue);
                        onChange(validation.minValue);
                        return;
                    }
                }

                if (
                    validation.minValue !== null &&
                    validation.minValue !== undefined &&
                    Number(inputValue) < Number(validation.minValue)
                ) {
                    if (inputValue !== validation.minValue) {
                        setInputValue(validation.minValue);
                        onChange(validation.minValue);
                    }
                    return;
                }

                if (
                    validation.maxValue !== null &&
                    validation.maxValue !== undefined &&
                    Number(inputValue) > Number(validation.maxValue)
                ) {
                    if (inputValue !== validation.maxValue) {
                        setInputValue(validation.maxValue);
                        onChange(validation.maxValue);
                    }
                    return;
                }
            }
        },
        [inputValue, validation, onChange]
    );

    const onValueChange = useCallback(
        (value, name, values) => {
            if (value === undefined || value === null || value === "") {
                setInputValue("");

                if (onChange) {
                    onChange("");
                }
                return;
            }

            if (onChange) {
                onChange(value);
            }

            setInputValue(value);
        },
        [onChange]
    );

    return (
        <FormGroup
            disabled={disabled}
            error={error}
            className="Number-input-wrapper"
        >
            {label && (
                <Label
                    htmlFor={inputId}
                    label={label}
                    labelInfobox={labelInfobox}
                    required={required}
                    helpText={helpText}
                />
            )}

            <div className="form-group">
                <div className="form-field">
                    <span className=" TextInput-group-wrapper">
                        <span className="TextInput-wrapper TextInput-group">
                            <span className="TextInput-affix-wrapper">
                                <CurrencyInput
                                    ref={inputRef}
                                    id={inputId}
                                    {...rest}
                                    disabled={disabled}
                                    defaultValue={defaultNumericValue}
                                    value={inputValue}
                                    className={numericInputClass}
                                    required={required}
                                    allowNegativeValue={inputAllowNegativeValue}
                                    suffix={suffix}
                                    prefix={prefix}
                                    allowDecimals={precision >= 1}
                                    decimalsLimit={precision}
                                    decimalSeparator={decimalSeparator}
                                    groupSeparator={thousandSeparator}
                                    onBlur={(e) => {
                                        onBlur(e);
                                    }}
                                    onValueChange={onValueChange}
                                    onFocus={onInputFocus}
                                >
                                    {children}
                                </CurrencyInput>
                            </span>
                            {addonAfter && (
                                <span className={classNameAddonAfter}>
                                    {addonAfter}
                                </span>
                            )}
                        </span>
                    </span>

                    {suffix && (
                        <span className="TextInput-suffix">
                            {hasSuffix && (
                                <Tooltip title={errorInTooltip ? error : null}>
                                    <TextInputIcon {...iconProps} />
                                </Tooltip>
                            )}
                        </span>
                    )}

                    {error && !errorInTooltip && (
                        <FormControlMessage>{error}</FormControlMessage>
                    )}

                    {children && <p>{children}</p>}
                </div>
            </div>
        </FormGroup>
    );
}


export default withTheme(NumberInput);
